/*!
 * vuex-connect v2.2.0
 * https://github.com/ktsn/vuex-connect
 *
 * Copyright (c) 2016-present katashin
 * Released under the MIT license
 * https://github.com/ktsn/vuex-connect/blob/master/LICENSE
 */
import Vue from 'vue';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

function merge() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    var target = {};
    args.forEach(function (obj) {
        Object.keys(obj).forEach(function (key) {
            target[key] = obj[key];
        });
    });
    return target;
}
function pick(obj, keys) {
    var res = {};
    keys.forEach(function (key) {
        if (obj[key] !== void 0) {
            res[key] = obj[key];
        }
    });
    return res;
}
function omit(obj, keys) {
    var res = {};
    Object.keys(obj).forEach(function (key) {
        if (!includes(keys, key)) {
            res[key] = obj[key];
        }
    });
    return res;
}
function flattenObject(obj) {
    return Object.keys(obj).reduce(function (acc, key) {
        return acc.concat(obj[key]);
    }, []);
}
function mapValues(obj, f) {
    var res = {};
    Object.keys(obj).forEach(function (key) {
        res[key] = f(obj[key], key);
    });
    return res;
}
function keys() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    return Object.keys(merge.apply(void 0, args));
}
function includes(array, item) {
    return array.indexOf(item) > -1;
}

var LIFECYCLE_KEYS = [
    'beforeCreate',
    'created',
    'beforeMount',
    'mounted',
    'beforeUpdate',
    'updated',
    'activated',
    'deactivated',
    'beforeDestroy',
    'destroyed'
];
function createConnect(transform) {
    return function connect(options) {
        if (options === void 0) { options = {}; }
        var _a = mapValues(options, normalizeOptions), _b = _a.stateToProps, stateToProps = _b === void 0 ? {} : _b, _c = _a.gettersToProps, gettersToProps = _c === void 0 ? {} : _c, _d = _a.actionsToProps, actionsToProps = _d === void 0 ? {} : _d, _e = _a.actionsToEvents, actionsToEvents = _e === void 0 ? {} : _e, _f = _a.mutationsToProps, mutationsToProps = _f === void 0 ? {} : _f, _g = _a.mutationsToEvents, mutationsToEvents = _g === void 0 ? {} : _g, _h = _a.methodsToProps, methodsToProps = _h === void 0 ? {} : _h, _j = _a.methodsToEvents, methodsToEvents = _j === void 0 ? {} : _j, _k = _a.lifecycle, lifecycle = _k === void 0 ? {} : _k;
        function connectToComponent(nameOrComponent, optionalComponent) {
            var _a;
            var Component, name;
            if (typeof nameOrComponent !== 'string') {
                Component = nameOrComponent;
                name = getOptions(Component).name || 'wrapped-anonymous-component';
            }
            else {
                Component = optionalComponent;
                name = nameOrComponent;
            }
            var propKeys = keys(stateToProps, gettersToProps, actionsToProps, mutationsToProps, methodsToProps);
            var eventKeys = keys(actionsToEvents, mutationsToEvents, methodsToEvents);
            var containerProps = omit(collectProps(Component), propKeys);
            var containerPropsKeys = Object.keys(containerProps);
            var options = {
                name: "connect-" + name,
                props: containerProps,
                components: (_a = {},
                    _a[name] = Component,
                    _a),
                computed: merge(mapState(stateToProps), mapGetters(gettersToProps)),
                methods: merge(mapActions(merge(actionsToProps, actionsToEvents)), mapMutations(merge(mutationsToProps, mutationsToEvents)), mapValues(merge(methodsToProps, methodsToEvents), bindStore))
            };
            insertLifecycleMixin(options, lifecycle);
            insertRenderer(options, name, propKeys.concat(containerPropsKeys), eventKeys);
            if (transform) {
                transform(options, lifecycle);
            }
            return typeof Component === 'function' ? Vue.extend(options) : options;
        }
        return connectToComponent;
    };
}
function insertRenderer(options, name, propKeys, eventKeys) {
    options.render = function (h) {
        var _this = this;
        return h(name, {
            props: pick(this, propKeys),
            on: mergeListeners(pick(this, eventKeys), this.$listeners || {}),
            scopedSlots: this.$scopedSlots
        }, flattenObject(this.$slots).map(function (slot) { return patchSlot(slot, _this); }));
    };
}
function insertLifecycleMixin(options, lifecycle) {
    options.mixins = [
        mapValues(pick(lifecycle, LIFECYCLE_KEYS), function (f) {
            return function boundLifecycle() {
                f.call(this, this.$store);
            };
        })
    ];
}
function mergeListeners(a, b) {
    Object.keys(b).forEach(function (key) {
        if (a.hasOwnProperty(key)) {
            a[key] = [].concat(a[key]).concat(b[key]);
        }
        else {
            a[key] = b[key];
        }
    });
    return a;
}
function getOptions(Component) {
    if (typeof Component === 'function') {
        return Component.options;
    }
    return Component;
}
/**
 * Collect all props options of the component.
 * It traverses all mixins and ancester components.
 */
function collectProps(Component) {
    var options = getOptions(Component);
    var supers = options.mixins || [];
    if (options.extends) {
        supers.push(options.extends);
    }
    var superProps = supers.reduce(function (acc, s) {
        return merge(acc, collectProps(s));
    }, {});
    var props = options.props || {};
    if (Array.isArray(props)) {
        props = props.reduce(function (acc, key) {
            acc[key] = null;
            return acc;
        }, {});
    }
    return merge(superProps, props);
}
function bindStore(fn) {
    return function boundFunctionWithStore() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return fn.call.apply(fn, [this, this.$store].concat(args));
    };
}
function normalizeOptions(options) {
    return Array.isArray(options)
        ? options.reduce(function (obj, value) {
            obj[value] = value;
            return obj;
        }, {})
        : options;
}
function patchSlot(vnode, vm) {
    if (vnode.context) {
        vnode.context = vm._self;
    }
    if (vnode.fnContext) {
        vnode.fnContext = vm._self;
    }
    return vnode;
}

var connect = createConnect();

export { connect, createConnect };
